import {
	UserLicense,
	UserLicenseViewModel,
	getEditViewModel
} from "client/Api/FeaturePackages/featurePackageManagement";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../../SettingsPage";
import { resolveFormattedTextString } from "client/Misc/utils";
import { createTryParseNumericEnum } from "client/Misc/parseUtils";
import { localize } from "client_legacy/ndaw/culture";
import { getUserLicenseName } from "client/Features/FeaturePackages/misc";
import {
	SearchInputForm,
	SearchPlaceholder,
	ToolbarAction
} from "client/Components";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	RouteParametersFromProps,
	connectToRouter
} from "client/Navigation";
import { UserGridItem } from "client/Api/UserManagement/userManagement";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { userLicenseManagementRouting } from "./UserLicenseManagementPage";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { pushMessage } from "client/Features/Alerts/pageReloadAlerts";
import { UserOverview } from "client/Features/Users/UserOverview";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { useManageUserLicensesDialog } from "../Licensing/ManageUserLicensesDialog";
import { reloadCurrentPage } from "client_legacy/ndaw/navigator";

export type EditUserLicensePageProps = {
	kind: UserLicense;
	onNavigate: NavigationCallback;
};

export const EditUserLicensePage = (props: EditUserLicensePageProps): ReactElement => {
	const {
		kind,
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [selectedUsers, setSelectedUsers] = useState<readonly UserGridItem[]>([]);
	const [usersAndUserGroupsData, setUsersAndUserGroupsData] = useState<UsersAndUserGroupsDataModel>({
		users: [],
		userGroups: []
	});

	const [changeLicenseDialog, openChangeLicenseDialog] = useManageUserLicensesDialog();

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	useActiveSettingsMenuItem(SettingsPageKind.FeaturePackages);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const model: UserLicenseViewModel = await getEditViewModel(kind, signal);
					setUsersAndUserGroupsData(model.allUsersAndUserGroupsData);
				}
				catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, kind]
	);

	const title = useMemo(
		(): string => {
			return resolveFormattedTextString({
				template: localize("_UsersForXPackage"),
				replacements: [
					{
						what: "{{Name}}",
						filler: getUserLicenseName(kind)
					}
				]
			});
		},
		[kind]
	);

	const usersWithThisLicense = useMemo(
		(): readonly UserGridItem[] => {
			return usersAndUserGroupsData.users.filter(
				(user) => user.userLicense === kind
			);
		},
		[kind, usersAndUserGroupsData.users]
	);

	const changeLicenseHandler = useCallback(
		async (userIds: readonly number[]): Promise<void> => {
			const result: TryResult<void> = await openChangeLicenseDialog({
				audience: { userIds },
				notAvailableLicense: kind
			});

			if (!result.wasSuccessful) {
				return;
			}

			pushMessage({
				text: localize("_ChangesSaved"),
				type: "success"
			});

			reloadCurrentPage();
		},
		[kind, openChangeLicenseDialog]
	);

	const deselectHandler = useCallback(
		(): void => {
			setSelectedUsers([]);
		},
		[]
	);

	const context = useMemo(
		(): Context => ({
			selectedUsers
		}),
		[selectedUsers]
	);

	const hasSelection: boolean = selectedUsers.length > 0;

	const selectionActions = useMemo(
		(): readonly ToolbarAction<Context>[] => {
			return listSelectionActions({ deselect: deselectHandler }, selectedUsers);
		},
		[deselectHandler, selectedUsers]
	);

	const contextualActions = useMemo(
		(): readonly ToolbarAction<Context>[] => {
			return listContextualActions({
				changeLicense: changeLicenseHandler
			});
		},
		[changeLicenseHandler]
	);

	const topBarActions = useMemo(
		(): readonly ToolbarAction<Context>[] => {
			return buildActionsForTopBar<Context, ToolbarAction<Context>>(
				{
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			contextualActions,
			hasSelection,
			isResponsive,
			selectionActions
		]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={context}
				title={title}
				hasSelection={hasSelection}
				onBack={() => onNavigate(userLicenseManagementRouting.overview)}
			>
				<SettingsPage.SearchSection>
					<SearchInputForm
						onChange={setSearchTerm}
						height="large"
						icon="filter"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={[
									localize("_Name"),
									localize("_Email"),
									localize("_Username")
								]}
							/>
						)}
						trackingId="filter"
					/>
				</SettingsPage.SearchSection>
				<SettingsPage.Section height="stretch">
					<UserOverview
						data={usersWithThisLicense}
						searchTerm={searchTerm}
						selection={selectedUsers}
						onSelectionChange={setSelectedUsers}
						height="stretch"
						hasNoUserLicenseFilteringAndSorting
					/>
				</SettingsPage.Section>
				<AiAssistantFabSpacing />
			</SettingsPage>
			{changeLicenseDialog}
		</>
	);
};
EditUserLicensePage.displayName = "EditUserLicensePage";

export type EditUserLicensePageParams = RouteParametersFromProps<
	EditUserLicensePageProps,
	"kind"
>;

export const EditUserLicensePageForRouter = connectToRouter(
	EditUserLicensePage,
	{
		"kind": {
			isRequired: true,
			tryParse: createTryParseNumericEnum(UserLicense)
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type Context = {
	selectedUsers: readonly UserGridItem[];
};

type SelectionActionsHandlers = {
	deselect(): void;
};

function listSelectionActions(
	handlers: SelectionActionsHandlers,
	selectedUsers: readonly UserGridItem[]
): readonly ToolbarAction<Context>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselect-all",
			title: deselectAllTitle(selectedUsers),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedUsers.length > 0,
			execute: () => handlers.deselect()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => c.selectedUsers.length > 0
		}
	];
}

type ContextualActionsHandlers = {
	changeLicense: (userIds: readonly number[]) => void;
};

function listContextualActions(
	handlers: ContextualActionsHandlers
): readonly ToolbarAction<Context>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "change-license",
			title: localize("_ChangePackage"),
			icon: "arrow-right-arrow-left",
			canExecute: (c) => c.selectedUsers.length > 0,
			execute: (c) => handlers.changeLicense(
				c.selectedUsers
					.map(u => u.id)
			)
		}
	];
}
